import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useLang } from "@/contexts/LanguageContext";
import { useT } from "@/lib/i18n";
import { HOME } from "@/constants/testIds";
import { api } from "@/lib/api";

const parseISO = (str) => {
    // "YYYY-MM-DD" -> Date at local midnight
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
};

const toISO = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const Availability = ({ products = [] }) => {
    const { lang } = useLang();
    const t = useT(lang);

    const jumpers = useMemo(
        () => products.filter((p) => (p.category || "").toLowerCase() === "jumpers"),
        [products],
    );

    const [productId, setProductId] = useState(null);
    const [bookedDates, setBookedDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [form, setForm] = useState({ name: "", phone: "", email: "", occasion: "", message: "", start_time: "12:00", end_time: "16:00" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (jumpers.length > 0 && !productId) setProductId(jumpers[0].id);
    }, [jumpers, productId]);

    const loadBooked = async (pid) => {
        try {
            const { data } = await api.get("/availability", { params: pid ? { product_id: pid } : {} });
            setBookedDates((data.booked_dates || []).map(parseISO));
        } catch (_e) {
            setBookedDates([]);
        }
    };

    useEffect(() => {
        if (productId) loadBooked(productId);
    }, [productId]);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!productId || !selectedDate) {
            toast.error(t("availability.form.pickFirst"));
            return;
        }
        if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
            toast.error(t("availability.form.required"));
            return;
        }
        setBusy(true);
        try {
            await api.post("/bookings", {
                product_id: productId,
                event_date: toISO(selectedDate),
                customer_name: form.name,
                phone: form.phone,
                email: form.email,
                occasion: form.occasion,
                message: form.message,
                start_time: form.start_time,
                end_time: form.end_time,
            });
            toast.success(t("availability.form.success"));
            setForm({ name: "", phone: "", email: "", occasion: "", message: "", start_time: "12:00", end_time: "16:00" });
            setSelectedDate(null);
            await loadBooked(productId);
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error(t("availability.form.conflict"));
                await loadBooked(productId);
            } else {
                toast.error(t("availability.form.error"));
            }
        } finally {
            setBusy(false);
        }
    };

    if (jumpers.length === 0) return null;

    const selectedJumper = jumpers.find((j) => j.id === productId);

    return (
        <section
            id="availability"
            data-testid={HOME.availabilitySection}
            className="relative py-28 md:py-40 overflow-hidden border-y"
            style={{ background: "var(--bg-primary)", borderColor: "rgba(46,16,37,0.08)" }}
        >
            <div className="mx-auto max-w-[1500px] px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-14 md:mb-20"
                >
                    <div className="md:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-8 h-px" style={{ background: "var(--teal)" }} />
                            <span className="font-body text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--teal)" }}>
                                {t("availability.eyebrow")}
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-6xl leading-[0.98] tracking-tight whitespace-pre-line" style={{ color: "var(--ink-1)" }}>
                            {t("availability.title")}
                        </h2>
                    </div>
                    <p className="md:col-span-5 md:col-start-8 self-end font-body text-lg md:text-xl leading-[1.55]" style={{ color: "var(--ink-2)" }}>
                        {t("availability.desc")}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
                    {/* Jumper selector */}
                    <div className="md:col-span-4">
                        <div className="font-body text-[11px] uppercase tracking-[0.28em] mb-4" style={{ color: "var(--ink-mute)" }}>
                            {t("availability.selectJumper")}
                        </div>
                        <div className="space-y-3">
                            {jumpers.map((j) => {
                                const active = productId === j.id;
                                return (
                                    <button
                                        key={j.id}
                                        data-testid={HOME.availabilityJumperOption(j.slug)}
                                        onClick={() => {
                                            setProductId(j.id);
                                            setSelectedDate(null);
                                        }}
                                        className="group w-full flex items-center gap-4 p-3 rounded-2xl text-left transition-colors"
                                        style={{
                                            background: active ? "#FFE4EE" : "#FFFDFC",
                                            border: `1px solid ${active ? "rgba(232,58,116,0.5)" : "rgba(46,16,37,0.1)"}`,
                                        }}
                                    >
                                        <span className="block w-14 h-14 shrink-0 rounded-xl overflow-hidden" style={{ background: "#FFF0F5" }}>
                                            {j.image && <img src={j.image} alt={j.title_en} className="w-full h-full object-cover" />}
                                        </span>
                                        <span className="flex-1">
                                            <span className="block font-serif text-lg leading-tight" style={{ color: "var(--ink-1)" }}>
                                                {lang === "es" ? j.title_es : j.title_en}
                                            </span>
                                            <span className="block font-body text-[10px] uppercase tracking-[0.24em] mt-1" style={{ color: "var(--ink-mute)" }}>
                                                {j.category}
                                            </span>
                                        </span>
                                        <span className="text-lg transition-transform" style={{ color: active ? "var(--pink)" : "var(--ink-mute)", transform: active ? "translateX(3px)" : "none" }}>
                                            →
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Legend */}
                        <div className="mt-6 flex flex-wrap gap-4 text-[11px] uppercase tracking-[0.24em]" style={{ color: "var(--ink-mute)" }}>
                            <span className="inline-flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full" style={{ background: "#FFF0F5", border: "1px solid rgba(46,16,37,0.2)" }} />
                                {t("availability.legendAvailable")}
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full" style={{ background: "var(--pink)" }} />
                                {t("availability.legendBooked")}
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full" style={{ background: "var(--ink-1)" }} />
                                {t("availability.legendSelected")}
                            </span>
                        </div>
                    </div>

                    {/* Calendar + form */}
                    <div className="md:col-span-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div>
                                <div className="font-body text-[11px] uppercase tracking-[0.28em] mb-4" style={{ color: "var(--ink-mute)" }}>
                                    {t("availability.selectDate")}
                                </div>
                                <div
                                    data-testid={HOME.availabilityCalendar}
                                    className="pck-cal p-4 rounded-3xl"
                                    style={{ background: "#FFFDFC", border: "1px solid rgba(46,16,37,0.1)" }}
                                >
                                    <DayPicker
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={setSelectedDate}
                                        disabled={[{ before: new Date(new Date().setHours(0, 0, 0, 0)) }, ...bookedDates]}
                                        modifiers={{ booked: bookedDates }}
                                        modifiersClassNames={{ booked: "pck-booked" }}
                                        weekStartsOn={0}
                                    />
                                </div>
                                <div className="font-body text-xs mt-3" style={{ color: "var(--ink-mute)" }}>
                                    {t("availability.dateHint")}
                                </div>
                            </div>

                            <form
                                onSubmit={submit}
                                className="p-6 md:p-8 rounded-3xl"
                                style={{ background: "#FFF0F5", border: "1px solid rgba(232,58,116,0.2)" }}
                            >
                                <div className="font-body text-[11px] uppercase tracking-[0.28em] mb-4 flex justify-between" style={{ color: "var(--ink-mute)" }}>
                                    <span>{selectedJumper ? (lang === "es" ? selectedJumper.title_es : selectedJumper.title_en) : "—"}</span>
                                    <span>{selectedDate ? selectedDate.toLocaleDateString(lang === "es" ? "es-US" : "en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
                                </div>

                                <div className="grid gap-4">
                                    <MiniField testId={HOME.availabilityFormName} label={t("availability.form.name")} value={form.name} onChange={(v) => set("name", v)} />
                                    <div className="grid grid-cols-2 gap-4">
                                        <MiniField testId={HOME.availabilityFormPhone} label={t("availability.form.phone")} value={form.phone} onChange={(v) => set("phone", v)} />
                                        <MiniField testId={HOME.availabilityFormEmail} label={t("availability.form.email")} value={form.email} onChange={(v) => set("email", v)} type="email" />
                                    </div>
                                    <MiniField testId={HOME.availabilityFormOccasion} label={t("availability.form.occasion")} value={form.occasion} onChange={(v) => set("occasion", v)} />
                                    <div className="grid grid-cols-2 gap-4">
                                        <MiniField label={t("availability.form.start")} value={form.start_time} onChange={(v) => set("start_time", v)} type="time" />
                                        <MiniField label={t("availability.form.end")} value={form.end_time} onChange={(v) => set("end_time", v)} type="time" />
                                    </div>
                                    <MiniArea testId={HOME.availabilityFormMessage} label={t("availability.form.message")} value={form.message} onChange={(v) => set("message", v)} />
                                    <button
                                        type="submit"
                                        data-testid={HOME.availabilityFormSubmit}
                                        disabled={busy}
                                        className="btn-pink mt-2 px-6 py-3.5 rounded-full text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                    >
                                        {busy ? t("availability.form.submitting") : `${t("availability.form.submit")} →`}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const MiniField = ({ label, value, onChange, testId, type = "text" }) => (
    <label className="block">
        <span className="font-body text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ink-mute)" }}>
            {label}
        </span>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="mt-1.5 block w-full rounded-md px-3 py-2.5 font-body text-sm border focus:outline-none focus:border-[var(--pink)] transition-colors"
            style={{ borderColor: "rgba(46,16,37,0.18)", background: "#FFFDFC", color: "var(--ink-1)" }}
        />
    </label>
);

const MiniArea = ({ label, value, onChange, testId }) => (
    <label className="block">
        <span className="font-body text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ink-mute)" }}>
            {label}
        </span>
        <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            data-testid={testId}
            className="mt-1.5 block w-full rounded-md px-3 py-2.5 font-body text-sm border resize-none focus:outline-none focus:border-[var(--pink)] transition-colors"
            style={{ borderColor: "rgba(46,16,37,0.18)", background: "#FFFDFC", color: "var(--ink-1)" }}
        />
    </label>
);

export default Availability;
